import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatError } from '../lib/api';
import { startGoogleRedirect } from '../lib/googleAuth';
import { LOGO_URL, SKETCH_PATTERNS } from '../lib/images';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

// Google icon SVG component
function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, loginAsGuest } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for error passed from auth redirect
  const callbackError = location.state?.error;

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Full-page Google OAuth redirect (Authorization Code flow). User lands back
  // at `${origin}/auth/google?code=...` after consent.
  const handleGoogleLogin = () => {
    setError('');
    setGoogleLoading(true);
    try {
      startGoogleRedirect('/');
    } catch (err) {
      setError('Google sign-in is not available. Please try another method.');
      setGoogleLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setGuestLoading(true);
    try {
      await loginAsGuest();
      navigate('/');
    } catch (err) {
      setError('Failed to continue as guest');
    } finally {
      setGuestLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)' }}>
      {/* Left Panel - Logo & Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center"
        style={{
          background: 'linear-gradient(160deg, #F2EFE9 0%, #E5E1D8 40%, #D4CBB3 100%)',
          backgroundImage: `url("${SKETCH_PATTERNS.leafBranch}")`,
          backgroundSize: '600px',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}>
        <div className="text-center px-12 animate-fade-in relative z-10">
          <img src={LOGO_URL} alt="Today" className="w-40 h-40 mx-auto mb-8 rounded-full shadow-lg"
            style={{ border: '4px solid rgba(255,255,255,0.6)' }} data-testid="login-logo" />
          <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Today</h1>
          <p className="text-base leading-relaxed max-w-sm mx-auto" style={{ color: 'var(--muted-fg)' }}>
            Your daily companion for wellness, mindfulness, and healthy living.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 opacity-20"
          style={{ backgroundImage: `url("${SKETCH_PATTERNS.waves}")`, backgroundRepeat: 'repeat-x', backgroundSize: '400px 100px', backgroundPosition: 'bottom' }} />
      </div>

      {/* Right Panel - Auth Options */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-slide-up">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-10">
            <img src={LOGO_URL} alt="Today" className="w-20 h-20 rounded-full object-cover mb-3 shadow-md" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Today</span>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-fg)' }}>Daily Reset</p>
          </div>

          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Welcome</h2>
          <p className="mb-8" style={{ color: 'var(--muted-fg)' }}>Start or continue your wellness journey</p>

          {/* Error Messages */}
          {(error || callbackError) && (
            <div className="mb-5 p-3 rounded-lg text-sm" data-testid="login-error"
              style={{ background: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' }}>
              {error || callbackError}
            </div>
          )}

          {/* PRIMARY — Google */}
          <button onClick={handleGoogleLogin} disabled={googleLoading} data-testid="google-login-btn"
            className="w-full py-4 rounded-lg font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] flex items-center justify-center gap-3 mb-3 disabled:opacity-60 text-base"
            style={{
              background: 'var(--text)',
              color: 'var(--bg)',
              boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
            }}>
            {googleLoading
              ? <Loader2 className="w-5 h-5 animate-spin" />
              : <span className="bg-white rounded-full p-1 -ml-1"><GoogleIcon /></span>}
            {googleLoading ? 'Signing in…' : 'Continue with Google'}
          </button>
          <p className="text-center text-[11px] mb-6" style={{ color: 'var(--muted-fg)' }}>
            Recommended — saves your progress across devices
          </p>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--muted-fg)' }}>or continue with email</span>
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
          </div>

          {/* SECONDARY — Email/password (always visible) */}
          <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-login-form">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text)' }}>Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                placeholder="you@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text)' }}>Password</label>
              <div className="relative">
                <input data-testid="login-password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full px-4 py-3 rounded-lg outline-none pr-12 transition-all"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                  placeholder="Enter your password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--muted-fg)' }}>
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full py-3 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In with Email
            </button>
            <p className="text-center text-sm" style={{ color: 'var(--muted-fg)' }}>
              Don't have an account?{' '}
              <Link to="/register" className="font-semibold hover:underline" style={{ color: 'var(--primary)' }} data-testid="goto-register">
                Create one
              </Link>
            </p>
          </form>

          {/* TERTIARY — Guest, intentionally tiny + far below */}
          <div className="mt-12 pt-6 text-center" style={{ borderTop: '1px dashed var(--border)' }}>
            <button onClick={handleGuestLogin} disabled={guestLoading} data-testid="guest-login-btn"
              className="text-xs underline-offset-2 hover:underline disabled:opacity-60 inline-flex items-center gap-1.5"
              style={{ color: 'var(--muted-fg)' }}>
              {guestLoading && <Loader2 className="w-3 h-3 animate-spin" />}
              {guestLoading ? 'Continuing…' : 'or just look around as a guest'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
